/**
 * 인증 쿠키의 이름과 스코프를 한 곳에서 정한다.
 *
 * 여기가 생긴 이유 — 같은 쿠키를 심는 곳(storage.ts)과 지우는 곳(middleware.ts)이
 * 서로 다른 도메인 스코프를 쓰고 있었다. 심을 때는 '.lawkit.kr', 지울 때는 도메인 없이.
 * 스코프가 다르면 브라우저는 다른 쿠키로 취급하므로 삭제가 조용히 실패한다.
 * 그 결과 무효가 된 세션이 영원히 남아, 헤더는 로그인 버튼을 그리는데
 * /sign-in 은 로그인 상태로 판단해 되돌려보내는 상태에 갇혔다.
 */

/** 프론트가 js-cookie 로 직접 심고 지우는 인증 쿠키 전체. */
export const AUTH_COOKIE_NAMES = ['LKAT', 'LKRT', 'LKUR', 'LKUN'] as const;

export type AuthCookieName = (typeof AUTH_COOKIE_NAMES)[number];

/**
 * 백엔드가 로그인·회원가입 응답에서 직접 심는 인증 쿠키.
 *
 * 프론트의 LK* 와는 별개 계열이다 — 이름도 스코프도 우리가 정하지 않았다
 * (lawkit-api 의 AuthCookieUtils: Domain=.lawkit.kr, HttpOnly, 운영에선 Secure+SameSite=None).
 *
 * HttpOnly 라 js-cookie 로는 지울 수 없다. 스크립트의 접근을 막는 것이지 서버가 못 지우는 게
 * 아니므로, Set-Cookie 를 직접 내보내는 미들웨어에서만 만료시킬 수 있다.
 * 세션을 접는다고 판정했으면 이쪽도 같이 접어야 한다 — 남겨두면 지난 로그인의 흔적이
 * 계속 함께 전송된다.
 */
export const SERVER_AUTH_COOKIE_NAMES = ['accessToken', 'refreshToken'] as const;

const ROOT_DOMAIN = 'lawkit.kr';

/** 서브도메인 간 로그인 공유를 위한 도메인 스코프. blog.lawkit.kr 에서도 같은 세션을 본다. */
export const SHARED_COOKIE_DOMAIN = `.${ROOT_DOMAIN}`;

/**
 * 쿠키를 심을 도메인을 호스트에서 유도한다.
 *
 * NODE_ENV 로 갈랐더니 두 가지가 곤란했다. 프리뷰 배포처럼 NODE_ENV=production 이지만
 * lawkit.kr 이 아닌 호스트에서 엉뚱한 도메인에 쿠키를 심으려 했고(브라우저가 거부한다),
 * 테스트에서는 프로덕션 경로 자체를 재현할 수 없었다. 호스트가 유일한 판단 근거다.
 *
 * 반환값은 항상 위 상수이고 host 를 그대로 돌려주지 않는다 —
 * Host 헤더는 클라이언트가 조작할 수 있으므로 값을 echo 하면 쿠키를 남의 도메인에 심게 된다.
 * 'evil-lawkit.kr' 은 '.lawkit.kr' 로 끝나지 않으므로 접미사 비교만으로 걸러진다.
 */
export function resolveAuthCookieDomain(hostname: string | null | undefined): string | undefined {
  if (!hostname) return undefined;

  const host = hostname.split(':')[0].trim().toLowerCase();
  if (host === ROOT_DOMAIN || host.endsWith(SHARED_COOKIE_DOMAIN)) return SHARED_COOKIE_DOMAIN;

  return undefined;
}
